import { Renderer, Vec2, Triangle, Transform, Program, Mesh, Texture, Color, TextureLoader } from 'ogl'
import { getPagePosition, clamp } from '@utils/utils'
import gsap from 'gsap'
import TagCursor from '@templates/tag-cursor.hbs'
import Flowmap from './Flowmap'
import BaseComponent from './_BaseComponent'



const damping = 0.93
const angularDamping = 0.98
const color  = '#a6ffef'
const color2 = '#a6ffef' // In case we need an outline color

/* Tagzone
---------------------------------------------------------------------------------------------------- */

export default class Tagzone extends BaseComponent {

    constructor($el, namespace) {
        super('TagZone', $el, namespace)


        this.createRenderer()
        this.createCursor()

        this.preload()
            .then(() => {
                this.createStage()
                this.resize()
                this.on()
            })
    }


    bindProperties() {
        this.aspect    = { value: 1 }
        this.size      = new Vec2()
        this.pos       = new Vec2()
        this.cursorpos = new Vec2()

        this.mouse     = new Vec2()
        this.prevMouse = new Vec2()
        this.acc       = new Vec2()
        this.vel       = new Vec2()
        this.angVel    = new Vec2(0.1, 0)

        this.isOut     = false
        this.isDown    = false

        this.isInit    = false
        this.isIntro   = false
    }


    bindHandlers() {
        this.resizeHandler       = () => this.onResize()
        this.pointerDownHandler  = e => this.onPointerDown(e)
        this.pointerMoveHandler  = e => this.onPointerMove(e)
        this.pointerUpHandler    = e => this.onPointerUp(e)
        this.pointerEnterHandler = e => this.onPointerEnter(e)
        this.pointerLeaveHandler = e => this.onPointerLeave(e)

        this.observeHandler      = e => this.onObserve(e)
        this.renderHandler       = (t, d, f) => this.render(t, d, f)
    }


    bindEvents() {
        const { $doc } = this.$utils

        this.addListeners(
            [$doc, 'layoutChange', this.resizeHandler],
            [this.$el, 'mousedown', this.pointerDownHandler],
            [this.$el, 'mousemove', this.pointerMoveHandler],
            [this.$el, 'mouseup', this.pointerUpHandler],
            [this.$el, 'mouseenter', this.pointerEnterHandler],
            [this.$el, 'mouseleave', this.pointerLeaveHandler],
        )

        this.observer = new IntersectionObserver(this.observeHandler)
    }


    on() {
        super.on()

        this.observer.observe(this.$el[0])
    }


    async preload() {
        const { gl } = this

        this.store = {
            noise: TextureLoader.load(gl, {
                src: '/static/img/misc/noise2.png',
                wrapS: gl.REPEAT,
                wrapT: gl.REPEAT,
                minFilter: gl.NEAREST,
                magFilter: gl.NEAREST,
            }),
        }
    }



    /* Init
    --------------------------------------------------------- */


    createRenderer() {
        this.renderer  = new Renderer({
            alpha: true,
            dpr: APP.Layout.pixelDensity,
            premultipliedAlpha: true,
            powerPreference: 'high-performance',
        })

        this.gl = this.renderer.gl

        this.$refs.wall.append(this.gl.canvas)
    }


    createCursor() {
        const $el  = parser.parseFromString(TagCursor(), 'text/html').body.firstChild
        const $svg    = $el.querySelector('.js-cursorTag-svg')
        const $circle = $el.querySelector('.js-cursorTag-circle')

        this.cursor   = { $el, $svg, $circle }
        this.$refs.wall.append($el)
    }


    createStage() {
        const { gl } = this

        // Create the scene object to render
        this.scene = new Transform()

        // Create the flowmap
        this.flowmap = new Flowmap(gl, {
            noiseTexture: this.store.noise,
            falloff: 0.08,
            dissipation: 0.003,
            color,
        })


        // Create the wall that will render the graffitis
        const geometry = new Triangle(gl)
        const program = new Program(gl, {
            vertex,
            fragment,
            transparent: true,
            uniforms: {
                tFlow: this.flowmap.uniform,
                tNoise: { value: this.store.noise },
                uTime: Time,
                uAspect: this.aspect,
                uColor: { value: new Color(color) },
                uFadeColor: { value: new Color(color2) },
            },
        })

        this.wall = new Mesh(gl, { geometry, program })
        this.wall.setParent(this.scene)
    }



    /* Handlers
    --------------------------------------------------------- */

    onResize() {
        this.resize()
    }


    onObserve([entry]) {
        const { isIntersecting } = entry

        this.toggleRender(isIntersecting)

        if (this.isInit) return
        this.playIntro()
    }


    onPointerDown(e) {
        if (!this.isInit) return

        this.flowmap.isDown = true
        this.isDown = true

        this.setPointerPosition(e.pageX, e.pageY)
    }


    onPointerMove(e) {
        if (!this.isInit) return

        this.setPointerPosition(e.pageX, e.pageY)
    }


    onPointerUp(e) {
        if (!this.isInit) return

        this.flowmap.isDown = false
        this.isDown = false
    }


    onPointerEnter() {
        if (!this.isInit) return

        this.isOut = false
    }


    onPointerLeave() {
        if (!this.isInit) return

        this.onPointerUp()

        this.isOut = true


        gsap.to(this.cursor.$el, {
            scale: 0,
            force3D: true,
            ease: 'expo.inOut',
            overwrite: true,
        })
    }



    /* Actions
    --------------------------------------------------------- */

    setPointerPosition(pageX, pageY) {
        if (this.isOut) return


        const [width, height] = this.size
        const [posX, posY] = this.pos

        const nx = ((pageX - posX) / width) * 2 - 1
        const ny = -((pageY - posY) / height) * 2 + 1

        this.mouse.set(nx, ny)
    }



    resize() {
        const { offsetWidth, offsetHeight } = this.$refs.wall[0]
        const { x, y } = getPagePosition(this.$refs.wall[0])

        this.aspect.value = offsetWidth / offsetHeight

        this.flowmap.aspect = this.aspect.value
        this.pos.set(x, y)
        this.size.set(offsetWidth, offsetHeight)

        this.flowmap.setSize(offsetWidth, offsetHeight)
        this.renderer.setSize(offsetWidth, offsetHeight)
    }


    toggleRender(shouldRender) {
        if (shouldRender) {
            gsap.ticker.add(this.renderHandler)
        } else {
            gsap.ticker.remove(this.renderHandler)
        }
    }


    playIntro() {
        const tl = gsap.timeline({
            onStart: () => {
                this.isIntro = true

                gsap.set(this.mouse, { x: -2, y: 0 })
                this.flowmap.isDown = true
            },

            onComplete: () => {
                this.isInit = true
                this.isIntro = false
                this.flowmap.isDown = false
            },

            // repeat: -1,
        })

        const initialFalloff = this.flowmap.falloff
        const initialDissip  = this.flowmap.dissipation

        tl.call(() => {
            this.flowmap.falloff = 0.06
            this.flowmap.dissipation = 0
        })

        tl.to(this.mouse, {
            motionPath: {
                path: 'M-0.322021 0.614502C-0.214706 0.663846 -0.100758 0.714226 0.134734 0.895502C0.370226 1.07678 0.57924 1.19604 0.629742 1.16507C0.678903 1.13491 0.542811 1.0344 0.479861 0.970432C0.41691 0.906467 0.325647 0.81783 0.352626 0.771227C0.379604 0.724624 0.559606 0.882709 0.666921 0.970432C0.774236 1.05816 0.965477 1.16233 0.996653 1.08557C1.02411 1.01796 0.959482 0.941191 0.931304 0.895502C0.903126 0.849813 0.822924 0.724242 0.851567 0.695384C0.886939 0.659747 0.965477 0.73559 1.05421 0.771227C1.14294 0.806865 1.23826 0.794986 1.28742 0.771227',
                offsetY: -0.5,
                offsetX: -1,
            },
            duration: 3.5,
            ease: 'power4.inOut',
        }, '+=0.2')

        tl.call(() => {
            this.flowmap.falloff     = initialFalloff
            this.flowmap.dissipation = initialDissip
        })
    }




    /* Values
    --------------------------------------------------------- */


    render(time, delta) {
        const { renderer: R } = this

        Time.value  = time
        Delta.value = delta


        this.updatePhysics()
        this.updateCursor()
        this.updateFlowmap()


        this.prevMouse.copy(this.mouse)

        // Render
        R.render({ scene: this.scene })
    }


    updatePhysics() {
        this.acc.sub(this.mouse, this.prevMouse)

        this.vel.add(this.acc)
        this.vel.multiply(damping)

        this.angVel.add(this.acc)
        this.angVel.multiply(angularDamping)
        this.angVel.clampLength(0.1, Infinity)
    }


    updateFlowmap() {
        this.flowmap.time = Time.value
        this.flowmap.mouse.copy(this.mouse)
        this.flowmap.prevMouse.copy(this.prevMouse)
        this.flowmap.velocity.copy(this.vel)

        this.flowmap.update()
    }


    updateCursor() {
        if (this.isOut) return

        const elScale = this.isDown ? 1.2 : 1
        const x = (this.mouse.x * 0.5 + 0.5) * this.size.x
        const y = (0.5 - this.mouse.y * 0.5) * this.size.y


        const scale    = Math.max(0.5, 1 - Math.abs(this.vel.x))
        const duration = this.isIntro ? 0.1 : 0.3

        gsap.to(this.cursor.$el, { x, y, duration, scale: elScale, force3D: true })
        gsap.set(this.cursor.$svg, { rotate: `+=${this.angVel.x * 10}`, scale, force3D: true })
        gsap.set(this.cursor.$circle, { scale, force3D: true })
    }

}




/* CONSTANTS & HELPERS
---------------------------------------------------------------------------------------------------- */
const parser = new DOMParser()
const Time    = { value: 0 }
const Delta   = { value: 0 }


const vertex = /* glsl */ `
    attribute vec2 uv;
    attribute vec2 position;
    varying vec2 vUv;


    void main() {
        vUv = uv;
        gl_Position = vec4(position, 0, 1);
    }
`

const fragment = /* glsl */ `
    precision highp float;

    uniform sampler2D tFlow;
    uniform sampler2D tNoise;

    uniform float uTime;
    uniform float uAspect;

    uniform vec3 uColor;
    uniform vec3 uFadeColor;
    varying vec2 vUv;

    void main() {
        vec2 uv = vUv;
        float flow = texture2D(tFlow, uv).r;

        float noise = texture2D(tNoise, uv * vec2(uAspect, 1.) * 16.).r * 2. - 1.;

        vec3 color = mix(uFadeColor, uColor, pow(flow, 2.));

        gl_FragColor = vec4(color * flow, flow) + noise * 0.1;
    }
`


/* Missing methods for OGL
--------------------------------------------------------- */

// eslint-disable-next-line func-names
Vec2.prototype.clampLength = function (min, max) {
    const len = this.len()

    return this.divide(len || 1).multiply(Math.max(min, Math.min(max, len)))
}


